import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { TableSkeleton } from "common-fe";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "i18n-fe";
import AppShell from "./AppShell";
import PageBreadcrumbs from "./PageBreadcrumbs";
import { useScoreContext } from "../context/ScoreContext";
import { useClassesForShow } from "../hooks/useClassesForShow";
import { useClassStatuses } from "../hooks/useClassStatuses";
import { useCatalogDefs } from "../hooks/useCatalogDefs";
import { useRingState } from "../hooks/useRingState";
import { scribePositionsFor } from "../util/scribeAssignment";
import {
  classDayKey,
  classEffectiveTime,
  classTestName,
  formatVenueTime,
  nextScheduledClass,
  partitionClassesByDone,
  venueTodayKey,
} from "../util/classDisplay";

// #1280 — keyed by DERIVED_CLASS_STATUSES (score-common). These used to be keyed
// by `Class.status`, a field classSchema never declared and nothing ever wrote,
// so every class fell through to the SCHEDULED default no matter its real state.
// Labels are i18n keys now, not literals.
const STATUS_COLORS = {
  SCHEDULED: { bg: "#e3f2fd", fg: "#1976d2", labelKey: "classes.status.scheduled" },
  IN_PROGRESS: { bg: "#fff3e0", fg: "#f57c00", labelKey: "classes.status.inProgress" },
  COMPLETE: { bg: "#e8f5e9", fg: "#388e3c", labelKey: "classes.status.complete" },
  PLACED: { bg: "#ede7f6", fg: "#5e35b1", labelKey: "classes.status.placed" },
};

export default function ClassList() {
  const navigate = useNavigate();
  const { t } = useTranslation("score");
  const { user } = useAuth();
  const sub = user?.profile?.sub ?? null;
  const { selectedShow } = useScoreContext();
  const { data: classes, loading, refetch, refreshing } = useClassesForShow(selectedShow?.id);
  // #1280 — real, ride-derived class state for the chip. Absent (still loading,
  // or offline with a cold cache) leaves the chip off entirely rather than
  // asserting "Scheduled" about a class we know nothing about.
  // #1557 — the same call now also yields each class's earliest ride time, for
  // the 84-of-189 classes that carry no scheduledTime of their own.
  const { data: classStatusData } = useClassStatuses(selectedShow?.id, classes);
  const classStatuses = classStatusData?.statuses;
  const earliestRideTimes = classStatusData?.earliestRideTimes;
  // card #1664 — table cache preferred over the bundled catalog for the
  // per-class test-name preview below (classTestName).
  const { catalogDefsById } = useCatalogDefs();

  // #1935/#2081 (docs/score/ring-state.md) — "current in ring" read-only
  // indicator (per class card, below). The Start class action itself moved
  // to the ride list page (#2081). Entirely optional and additive: nothing
  // here gates rendering or interaction with the rest of this screen (AC6).
  const ringState = useRingState(selectedShow?.id);

  // #910 — a scribe almost always only cares about TODAY, not every day of a
  // multi-day show. Default ON; toggling off reveals every assigned class
  // regardless of day. "Today" is the VENUE's wall-clock day
  // (America/Los_Angeles, #688) — never the scribe device's own zone — so
  // this compares classDayKey() against venueTodayKey(), both now the SAME
  // real timezone conversion (#1559; they used to be two different
  // derivations — a naive slice for the class, a real conversion for "now"
  // — which is how an evening class silently fell off this view). Computed
  // once per render, not per class, so a long list doesn't re-derive "today"
  // per row.
  const [todayOnly, setTodayOnly] = useState(true);
  const visibleClasses = useMemo(() => {
    const all = classes ?? [];
    if (!todayOnly) return all;
    const todayKey = venueTodayKey();
    return all.filter((c) => classDayKey(c) === todayKey);
  }, [classes, todayOnly]);

  // #1557 — the next class with no scores in it, for the session-header chip.
  // Computed over visibleClasses, not the full list, so it honours the
  // today-only switch: an unscored class left behind on an EARLIER day would
  // otherwise stay "next" forever, which is the same species of permanently
  // wrong chip this card replaced. Order is untouched (visibleClasses is a
  // filter over useClassesForShow's scheduledTime sort), so "earliest" still
  // means earliest.
  const nextClass = useMemo(
    () => nextScheduledClass(visibleClasses, classStatuses),
    [visibleClasses, classStatuses],
  );
  const nextClassTime = nextClass
    ? classEffectiveTime(nextClass, earliestRideTimes)
    : null;

  // #135 / #1702 — split the (already time-ordered) visible list so a
  // scribe never has to scan past finished classes to reach the next one to
  // score: still-to-score classes render first, Completed/Placed classes
  // render below a divider, keeping their existing chronological order in
  // both halves. See partitionClassesByDone for the "done" definition.
  const { active: activeClasses, done: doneClasses } = useMemo(
    () => partitionClassesByDone(visibleClasses, classStatuses),
    [visibleClasses, classStatuses],
  );

  const renderClassCard = (c) => {
    const rawStatus = classStatuses?.[c.id];
    // #1935 — "already current for its ring", independent of how it got
    // there (Start class, or simply the first ride of this class being
    // scored, #1934's SCORED path). #2109 (docs/score/ring-truth.md) — this
    // now goes through useRingState.isCurrentForRing, which itself calls
    // score-common's resolveRingTruth: the ONE source of "in progress" this
    // screen reads.
    const isCurrentInRing = ringState.isCurrentForRing(c.ring, c.id);
    // #2109 — the status chip's IN_PROGRESS label used to come straight off
    // deriveClassStatus (rawStatus), which is only "at least one ride is
    // past PENDING" (score-common/ride-queue.js). That is exactly #2083's
    // 9am-ghost bug: a class the scribe scored earlier and then moved away
    // from keeps its scoresheets open, so rawStatus stays IN_PROGRESS
    // forever even though the ring has long since moved on. The chip now
    // shows "In progress" only when the CENTRAL ring record says this class
    // is the ring's live occupant right now (suppressed once the class is
    // actually COMPLETE/PLACED, same as the ring-current chip below); a
    // rawStatus of IN_PROGRESS that the ring record doesn't confirm renders
    // no chip at all rather than repeating the stale claim (rule 8: no
    // consumer invents a status).
    const displayStatusKey =
      isCurrentInRing && rawStatus !== "COMPLETE" && rawStatus !== "PLACED"
        ? "IN_PROGRESS"
        : rawStatus === "IN_PROGRESS"
          ? null
          : rawStatus;
    const status = STATUS_COLORS[displayStatusKey] ?? null;
    // #627 — the seat this scribe is assigned in THIS class. Normally one;
    // a scribe named at two seats of the same class picks on entry.
    const positions = scribePositionsFor(c, sub);
    // #896 — full test name on the card, so the scribe can see/confirm
    // which test the class rides without opening it. Omitted (not a
    // placeholder) on a Test-of-Choice class with no single test hint.
    const testName = classTestName(c, catalogDefsById);
    return (
      <Card
        key={c.id}
        variant="outlined"
        sx={{
          borderRadius: 3,
          transition: "box-shadow 0.2s, border-color 0.2s",
          "&:hover": {
            borderColor: "primary.main",
            boxShadow: 4,
          },
        }}
      >
        <CardActionArea
          data-testid="class-card"
          onClick={() => navigate(`/classes/${c.id}`)}
          sx={{ p: 0 }}
        >
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box>
                <Typography
                  variant="subtitle1"
                  color="primary"
                  fontWeight={700}
                >
                  {formatVenueTime(c.scheduledTime)}
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {c.classNumber != null && `${c.classNumber}`.trim() !== ""
                    ? `${c.classNumber}. ${c.className}`
                    : c.className}
                </Typography>
                {testName && (
                  <Typography
                    variant="body2"
                    data-testid="class-test-name"
                    sx={{ color: "text.secondary" }}
                  >
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {t("rides.test")}:
                    </Box>{" "}
                    {testName}
                  </Typography>
                )}
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ mt: 1, color: "text.secondary" }}
                  divider={<span>•</span>}
                >
                  {c.ring && (
                    <Typography variant="body2">{c.ring}</Typography>
                  )}
                  {positions.length > 0 && (
                    <Typography variant="body2" fontWeight={600}>
                      Position {positions.join(" / ")}
                    </Typography>
                  )}
                </Stack>
              </Box>
              {status && (
                <Chip
                  data-testid="class-status"
                  label={t(status.labelKey)}
                  sx={{
                    bgcolor: status.bg,
                    color: status.fg,
                    fontWeight: 600,
                  }}
                />
              )}
            </Stack>
          </CardContent>
        </CardActionArea>

        {/* #1935/#2081 — "current in ring" indicator, OUTSIDE
            CardActionArea (RideList.jsx's discard-button pattern, so it
            never intercepts a tap into the class). The START CLASS action
            itself moved to the ride list page (#2081, beside Add Ride
            Manually) — this stays behind as a read-only status: a class
            already running in its ring shows the chip, otherwise nothing.
            Suppressed on a COMPLETE/PLACED class (docs/score/ring-state.md)
            same as before, since a finished class can't be "current". */}
        {c.ring && isCurrentInRing && rawStatus !== "COMPLETE" && rawStatus !== "PLACED" && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Chip
              size="small"
              color="success"
              variant="outlined"
              data-testid={`class-ring-current-${c.id}`}
              label={t("classes.ringState.current", { ring: c.ring })}
            />
          </Box>
        )}
      </Card>
    );
  };

  return (
    <AppShell
      showNextClass
      nextClass={nextClass}
      nextClassTime={nextClassTime}
      // #2196 — nextClass is derived from `classes`/`classStatuses`, so the
      // header's "No upcoming class" must stay a loading chip until BOTH of
      // those fetches have actually settled, not flash a confident negative
      // off an empty in-flight list. `classStatusData === undefined` (not a
      // separate `classStatusLoading` flag) is the real signal here: useClassStatuses
      // is keyed on the class list, which starts empty, so its own `loading`
      // flag settles false the instant that empty pass resolves and never
      // resets when the list fills in and the real (slow) status fetch
      // starts (#2162) — see useClassStatuses.js's fetchRemote guard, which
      // now returns `undefined` for exactly this reason.
      // The `(classes ?? []).length > 0` half is what stops that sentinel from
      // becoming a PERMANENT spinner on a show with zero classes: with an empty
      // list BOTH of useClassStatuses' branches return `undefined` by design, so
      // nothing ever settles it and the chip would spin forever, which is the
      // exact defect this card exists to remove. The sentinel is only meaningful
      // once there is at least one class whose status could still be in flight;
      // `loading` already covers the window before the class list itself has
      // settled, so a list that has settled EMPTY correctly reads as a known
      // "No upcoming class". Gating here rather than making the hook's sentinel
      // list-aware keeps the whole rule in one file, next to the `classes` and
      // `classStatuses` that nextClass is actually derived from, instead of
      // resting on useClassesForShow's null-vs-empty-array convention.
      nextClassLoading={loading || ((classes ?? []).length > 0 && classStatusData === undefined)}
    >
      <PageBreadcrumbs items={[{ label: t("classes.title") }]} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {t("classes.title")}
        </Typography>
        <FormControlLabel
          sx={{ color: "text.primary", mr: 0 }}
          control={
            <Switch
              data-testid="today-only-switch"
              checked={todayOnly}
              onChange={(e) => setTodayOnly(e.target.checked)}
            />
          }
          label={t("classes.todayOnly")}
        />
      </Stack>

      {/* #888 — mid-show scribe reassignment sits invisible behind the
          IndexedDB cache (useClassesForShow only revalidates on mount/dep
          change) until a full app reload re-mounts the hook. This is the
          one kiosk-appropriate escape hatch: a manual refresh that forces
          useCachedResource's refetch() past the cache. Sticky (top:105 —
          same AppBar(64)+SessionHeader offset ScoringForm's sticky bar
          uses, #647) and pinned top-left of the list so it stays reachable
          while scrolling a long class list, without competing with the
          page title. Own bg/border/shadow (not color="primary" — brand
          primary.main is a near-black grey that all but disappears against
          a dark-mode background, #862) so it reads clearly in both themes. */}
      <Box
        sx={{
          position: "sticky",
          top: 105,
          zIndex: 1100,
          display: "flex",
          justifyContent: "flex-start",
          mb: 1,
        }}
      >
        <Tooltip title={refreshing ? t("classes.refreshing") : t("classes.refresh")}>
          <span>
            <IconButton
              data-testid="refresh-classes-button"
              aria-label={t("classes.refresh")}
              onClick={() => {
                refetch();
                // #1935 — the manual refresh also picks up a ring's state
                // changing from elsewhere (another scribe's Start class,
                // Score Admin, or a scoring write), same escape hatch as
                // the class list's own cache.
                ringState.refetch();
              }}
              disabled={refreshing}
              sx={{
                bgcolor: "background.paper",
                color: "text.primary",
                border: 1,
                borderColor: "divider",
                boxShadow: 2,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <RefreshIcon
                fontSize="small"
                sx={
                  refreshing
                    ? {
                        animation: "classListRefreshSpin 1s linear infinite",
                        "@keyframes classListRefreshSpin": {
                          from: { transform: "rotate(0deg)" },
                          to: { transform: "rotate(360deg)" },
                        },
                      }
                    : undefined
                }
              />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* #714 — card list not yet loaded: shaped skeleton rows, not a bare
          centered spinner, so the screen reads as "content coming" rather
          than "something is broken/blank". */}
      {loading && (
        <Box sx={{ mt: 2 }}>
          <TableSkeleton rows={5} dense={false} />
        </Box>
      )}

      {!loading && (classes ?? []).length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 3 }}>
          {t("classes.empty")}
        </Typography>
      )}

      {/* #910 — the scribe HAS assigned classes, just none scheduled today
          (e.g. viewing the class list the evening before a show, or after the
          venue's last class of the day). Distinct from classes.empty above
          (no assignments at all) so the hint to turn the switch off only
          shows when it would actually reveal something. */}
      {!loading && (classes ?? []).length > 0 && visibleClasses.length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 3 }}>
          {t("classes.emptyToday")}
        </Typography>
      )}

      <Stack spacing={2} sx={{ mt: 2 }} data-testid="active-classes">
        {activeClasses.map(renderClassCard)}
      </Stack>

      {/* #135 / #1702 — Completed/Placed classes stay visible and openable
          (a scribe does go back into a placed class to check something),
          just pushed below a labeled divider so they never sit ahead of the
          next class still to score. Only rendered once there IS a done
          class — an all-active list shows no divider at all. */}
      {doneClasses.length > 0 && (
        <>
          <Divider
            data-testid="completed-divider"
            sx={{ mt: activeClasses.length > 0 ? 4 : 2, mb: 2 }}
          >
            <Typography variant="overline" color="text.secondary">
              {t("classes.completedDivider")}
            </Typography>
          </Divider>
          <Stack spacing={2} data-testid="done-classes">
            {doneClasses.map(renderClassCard)}
          </Stack>
        </>
      )}
    </AppShell>
  );
}
